import { NavLink, Outlet, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { useAuth } from "@/lib/team";
import {
  ChartBar, UsersThree, PaperPlaneTilt, Calendar, EnvelopeSimple,
  GearSix, LinkSimple, Compass, MapPin, UsersFour, CaretDown, SignOut, GitBranch, Devices
} from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";

const NavItem = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    end
    data-testid={testId}
    className={({ isActive }) =>
      `group flex items-center gap-3 px-3 py-2 rounded-md transition-colors duration-150 text-sm ${
        isActive
          ? "bg-neutral-900 text-white"
          : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
      }`
    }
  >
    <Icon size={18} weight="duotone" />
    <span className="font-medium tracking-tight">{label}</span>
  </NavLink>
);

function UserBadge() {
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="w-full flex items-center gap-2 p-2 border border-neutral-200 hover:border-neutral-900 rounded-md transition-colors" data-testid="user-badge">
          <div className="w-7 h-7 rounded-full grid place-items-center text-white text-[10px] font-mono" style={{ background: user.color }}>
            {user.initials}
          </div>
          <div className="flex-1 text-left overflow-hidden">
            <div className="text-xs text-neutral-500 font-mono uppercase tracking-widest">Signed in</div>
            <div className="text-sm font-medium truncate">{user.name}</div>
          </div>
          <CaretDown size={12} className="text-neutral-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="start">
        <DropdownMenuLabel>
          <div className="font-medium">{user.name}</div>
          <div className="text-xs text-neutral-500 font-mono truncate">{user.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/team" data-testid="manage-team-link">Manage team →</Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={logout} data-testid="logout-btn">
          <SignOut size={14} className="mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen flex bg-[#FAFAF7]" data-testid="app-shell">
      <aside className="w-64 border-r border-neutral-200 bg-white flex-shrink-0 flex flex-col">
        <div className="px-5 py-6 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-neutral-900 grid place-items-center rounded">
              <Compass size={18} weight="fill" color="white" />
            </div>
            <div>
              <div className="font-display text-xl leading-none">Prospexus</div>
              <div className="overline mt-1">Outreach OS</div>
            </div>
          </div>
        </div>

        <div className="px-3 pt-4">
          <UserBadge />
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <div className="overline px-3 pb-2">Workspace</div>
          <NavItem to="/" icon={ChartBar} label="Dashboard" testId="nav-dashboard" />
          <NavItem to="/prospects" icon={UsersThree} label="Prospects" testId="nav-prospects" />
          <NavItem to="/territory" icon={MapPin} label="Territory Map" testId="nav-territory" />
          <NavItem to="/campaigns" icon={PaperPlaneTilt} label="Campaigns" testId="nav-campaigns" />
          <NavItem to="/inbox" icon={EnvelopeSimple} label="Inbox" testId="nav-inbox" />
          <NavItem to="/appointments" icon={Calendar} label="Appointments" testId="nav-appointments" />

          <div className="overline px-3 pt-6 pb-2">Configure</div>
          <NavItem to="/team" icon={UsersFour} label="Team" testId="nav-team" />
          <NavItem to="/rules" icon={GitBranch} label="Assignment Rules" testId="nav-rules" />
          <NavItem to="/sessions" icon={Devices} label="Signed-in Devices" testId="nav-sessions" />
          <NavItem to="/booking-link" icon={LinkSimple} label="Booking Link" testId="nav-booking-link" />
          <NavItem to="/settings" icon={GearSix} label="Settings" testId="nav-settings" />
        </nav>

        <div className="px-5 py-4 border-t border-neutral-200">
          <div className="overline mb-1">Region</div>
          <div className="text-xs text-neutral-500 font-mono">Global · UTC</div>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <Outlet />
      </main>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}
